import classNames from 'classnames';

import { Panel } from './panel';

const KPI_TILE_COUNT = 4;
const CHART_COLUMN_COUNT = 12;
const TABLE_ROW_COUNT = 6;
const FIXTURE_CELL_COUNT = 6;
const THREAT_ROW_COUNT = 6;

/**
 * Fixed silhouette for the loading chart. Hard-coded rather than random so the
 * server and client renders match.
 */
const SKELETON_BAR_HEIGHTS = [45, 70, 35, 85, 55, 40, 75, 30, 60, 50, 80, 42];

type ShimmerProps = {
  className?: string;
  style?: React.CSSProperties;
};

const Shimmer = ({ className, style }: ShimmerProps) => (
  <div
    className={classNames('animate-pulse rounded bg-neutral-200', className)}
    style={style}
  />
);

export const HeroSkeleton = () => (
  <div className="border-b border-neutral-200 bg-white">
    <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 pt-5 pb-7 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-12">
      <div className="flex items-end gap-6">
        <Shimmer className="h-[116px] w-[116px] rounded-full" />
        <div className="flex flex-col gap-3">
          <Shimmer className="h-11 w-64" />
          <Shimmer className="h-6 w-48" />
        </div>
      </div>
      <div className="grid w-full grid-cols-2 gap-px overflow-hidden rounded-xl border border-neutral-200 bg-neutral-200 lg:w-[340px]">
        {Array.from({ length: KPI_TILE_COUNT }, (_unused, index) => (
          <div key={index} className="bg-white px-4 py-3.5">
            <Shimmer className="h-3 w-20" />
            <Shimmer className="mt-2 h-7 w-16" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ChartSkeleton = () => (
  <Panel>
    <div className="px-5 pt-5 pb-4 sm:px-6">
      <Shimmer className="h-5 w-52" />
      <Shimmer className="mt-2 h-4 w-72" />
    </div>
    <div className="flex h-[190px] items-end gap-2.5 border-t border-neutral-200 px-5 pb-5 pt-6 sm:px-6">
      {Array.from({ length: CHART_COLUMN_COUNT }, (_unused, index) => (
        <Shimmer
          key={index}
          className="w-full"
          style={{ height: `${SKELETON_BAR_HEIGHTS[index]}%` }}
        />
      ))}
    </div>
  </Panel>
);

export const MetricRowsSkeleton = () => (
  <Panel>
    <div className="px-5 pt-5 pb-4 sm:px-6">
      <Shimmer className="h-5 w-44" />
      <Shimmer className="mt-2 h-4 w-80" />
    </div>
    <div className="border-t border-neutral-200 px-5 sm:px-6">
      {Array.from({ length: THREAT_ROW_COUNT }, (_unused, index) => (
        <div
          key={index}
          className="flex items-center gap-4 border-b border-neutral-100 py-3.5 last:border-b-0"
        >
          <Shimmer className="h-4 w-40" />
          <Shimmer className="h-6 w-16" />
          <Shimmer className="h-2 flex-1" />
          <Shimmer className="h-4 w-10" />
        </div>
      ))}
    </div>
  </Panel>
);

export const FixturesSkeleton = () => (
  <Panel>
    <div className="px-5 pt-5 pb-4 sm:px-6">
      <Shimmer className="h-5 w-44" />
    </div>
    <div className="grid grid-cols-2 gap-px border-t border-neutral-200 bg-neutral-200 md:grid-cols-3 lg:grid-cols-6">
      {Array.from({ length: FIXTURE_CELL_COUNT }, (_unused, index) => (
        <div key={index} className="bg-white px-4 pt-4 pb-4">
          <Shimmer className="h-3 w-10" />
          <Shimmer className="mt-2 h-6 w-16" />
          <Shimmer className="mt-3 h-7 w-12" />
        </div>
      ))}
    </div>
  </Panel>
);

export const TableSkeleton = () => (
  <Panel>
    <div className="px-5 pt-5 pb-4 sm:px-6">
      <Shimmer className="h-5 w-40" />
    </div>
    <div className="border-t border-neutral-200">
      {Array.from({ length: TABLE_ROW_COUNT }, (_unused, index) => (
        <div
          key={index}
          className="flex items-center gap-4 border-b border-neutral-100 px-5 py-3 last:border-b-0 sm:px-6"
        >
          <Shimmer className="h-4 w-10" />
          <Shimmer className="h-4 flex-1" />
          <Shimmer className="h-4 w-12" />
          <Shimmer className="h-4 w-12" />
        </div>
      ))}
    </div>
  </Panel>
);

/** Full-page loading state, replacing the old "Loading player…" text. */
export const ProfileSkeleton = () => (
  <div className="min-h-screen bg-gray-50">
    <HeroSkeleton />
    <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-5 sm:px-6 lg:px-12">
      <ChartSkeleton />
      <MetricRowsSkeleton />
      <FixturesSkeleton />
      <TableSkeleton />
    </div>
  </div>
);
